<app-loading
    [observable]="storefront$"
>
    <ng-container *ngIf="storefront$ | async as storefront">
        <div
            class="min-vh-100 py-3 py-lg-4"
            style="background: linear-gradient(180deg, var(--ts-surface-subtle) 0%, var(--ts-surface-light) 100%); overflow-wrap: anywhere;"
        >
            <div class="container vstack gap-4 gap-lg-5">
                <div class="vstack gap-3">
                    <a
                        [routerLink]="storefront.landingPagePath"
                        class="btn btn-link p-0 text-decoration-none align-self-start fw-semibold"
                        style="color: var(--ts-content-base);"
                    >
                        <i class="bi bi-arrow-left me-2"></i>
                        Back to venue page
                    </a>

                    <div class="row g-3 g-lg-4 align-items-end">
                        <div class="col-12 col-lg-8">
                            <div class="vstack gap-2">
                                <span
                                    class="small fw-semibold text-uppercase"
                                    style="color: var(--ts-content-subtle); letter-spacing: 0.08em;"
                                >
                                    Gift Vouchers
                                </span>
                                <h1 class="display-6 fw-semibold mb-0">
                                    {{ storefront.venue.voucherDisplayName() }}
                                </h1>
                                <p class="mb-0 text-secondary col-lg-10">
                                    Browse the gift vouchers available for this venue, compare the options, and select the one you want to purchase.
                                </p>
                            </div>
                        </div>

                        @if (storefront.venue.shouldShowVoucherStorefrontAddressCard()) {
                            <div class="col-12 col-lg-4">
                                <div
                                    class="card border-0 shadow-sm h-100"
                                    data-testid="voucher-storefront-address-card"
                                    style="background-color: var(--ts-surface-base-inverse);"
                                >
                                    <div class="card-body vstack gap-2">
                                        <div class="hstack gap-2 align-items-baseline text-secondary">
                                            <i class="bi bi-geo-alt mt-1"></i>
                                            <span>{{ storefront.venue.address | addressString }}</span>
                                        </div>
                                        @if (storefront.venue.phoneNumber) {
                                            <div class="hstack gap-2 align-items-baseline text-secondary">
                                                <i class="bi bi-telephone mt-1"></i>
                                                <a
                                                    [href]="'tel:' + storefront.venue.phoneNumber"
                                                    class="link-secondary text-decoration-none"
                                                >
                                                    {{ storefront.venue.phoneNumber }}
                                                </a>
                                            </div>
                                        }
                                        @if (storefront.venue.emailAddress) {
                                            <div class="hstack gap-2 align-items-baseline text-secondary">
                                                <i class="bi bi-envelope mt-1"></i>
                                                <a
                                                    [href]="'mailto:' + storefront.venue.emailAddress"
                                                    class="link-secondary text-decoration-none text-break"
                                                >
                                                    {{ storefront.venue.emailAddress }}
                                                </a>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                </div>

                @if (!storefront.canPurchaseVouchers) {
                    <div
                        class="rounded-3 p-3 bg-warning-subtle border border-warning mb-0"
                        role="status"
                    >
                        <p class="fw-semibold mb-1">
                            Gift vouchers are unavailable to buy online right now.
                        </p>
                        <p class="text-secondary mb-0">
                            Please contact {{ storefront.venue.voucherDisplayName() }} directly for gift voucher availability.
                        </p>
                    </div>
                }

                @if (storefront.vouchers.length > 0) {
                    <div class="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3 g-lg-4">
                        @for (voucherItem of storefront.vouchers; track voucherItem.voucher.id) {
                            @let voucher = voucherItem.voucher;
                            <div class="col d-flex">
                                <article
                                    class="card border-0 shadow-sm h-100 w-100"
                                    style="background-color: var(--ts-surface-base-inverse); overflow-wrap: anywhere;"
                                >
                                    @if (voucher.imageId) {
                                        <div
                                            class="position-relative w-100 overflow-hidden"
                                            [style.aspect-ratio]="voucherImageAspectRatio(voucher.id)"
                                            style="max-height: 20rem;"
                                        >
                                            <img
                                                [ngSrc]="environment.cdnUrl | thumbor : 'voucher-images/' + voucher.imageId : { width: 960, height: 960, aspectFit: true }"
                                                [alt]="voucher.displayName || storefront.venue.voucherDisplayName()"
                                                class="card-img-top object-fit-cover"
                                                fill
                                                decoding="async"
                                                loading="lazy"
                                                sizes="(min-width: 1200px) 33vw, (min-width: 768px) 50vw, 100vw"
                                                (load)="onVoucherImageLoaded(voucher.id, $event)"
                                            />
                                        </div>
                                    }
                                    <div class="card-body p-3 p-lg-4 d-flex flex-column gap-3">
                                        <div class="d-flex justify-content-between align-items-start gap-3">
                                            <div class="vstack gap-2">
                                                <h2 class="h5 fw-semibold mb-0">{{ voucher.displayName }}</h2>
                                            </div>

                                            @if (!voucher.imageId) {
                                                <div
                                                    class="rounded-circle d-inline-flex justify-content-center align-items-center flex-shrink-0"
                                                    style="width: 3rem; height: 3rem; background-color: var(--ts-surface-subtle); color: var(--ts-primary-content-base);"
                                                >
                                                    <i
                                                        class="bi bi-ticket-perforated fs-4"
                                                        appVenueColour
                                                        [primaryColour]="storefront.venue.primaryColour"
                                                        style="color: var(--app-primary-colour);"
                                                    >
                                                    </i>
                                                </div>
                                            }
                                        </div>

                                        @if (voucherItem.descriptionContent) {
                                            <div
                                                class="quill-flush text-secondary"
                                            >
                                                <quill-view
                                                    [content]="voucherItem.descriptionContent"
                                                >
                                                </quill-view>
                                            </div>
                                        }

                                        <div class="row g-2 small">
                                            <div class="col-6 d-flex">
                                                <div
                                                    class="rounded-3 p-3 w-100"
                                                    style="background-color: var(--ts-surface-subtle);"
                                                >
                                                    <div
                                                        class="text-uppercase fw-semibold small"
                                                        style="color: var(--ts-content-subtle); letter-spacing: 0.08em;"
                                                    >
                                                        From
                                                    </div>
                                                    <div class="fw-semibold fs-5 mt-1">
                                                        {{ voucherItem.formattedLowestPrice }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="col-6 d-flex">
                                                <div
                                                    class="rounded-3 p-3 w-100"
                                                    style="background-color: var(--ts-surface-subtle);"
                                                >
                                                    <div
                                                        class="text-uppercase fw-semibold small"
                                                        style="color: var(--ts-content-subtle); letter-spacing: 0.08em;"
                                                    >
                                                        Expiry
                                                    </div>
                                                    @if (voucher.expiryMonths) {
                                                        <div class="fw-semibold mt-1 fs-5">
                                                            {{ voucher.expiryMonths }} month@if (voucher.expiryMonths > 1) {s }
                                                        </div>
                                                    } @else {
                                                        @if (voucherItem.expiryDate; as voucherExpiryDate) {
                                                            <div class="fw-semibold mt-1 fs-5">
                                                                {{ voucherExpiryDate | date:'d MMM yyyy' }}
                                                            </div>
                                                        } @else {
                                                            <div class="fw-semibold mt-1 fs-5">
                                                                Flexible
                                                            </div>
                                                        }
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        <div class="d-grid mt-auto">
                                            <button
                                                appVenueColour
                                                class="btn btn-primary"
                                                type="button"
                                                data-testid="landing-buy-voucher"
                                                [primaryColour]="storefront.venue.primaryColour"
                                                [disabled]="!storefront.canPurchaseVouchers"
                                                (click)="openVoucherPurchase(storefront, voucher.id)"
                                            >
                                                Buy Voucher
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        }
                    </div>
                } @else {
                    <div
                        class="card border-0 shadow-sm"
                        style="background-color: var(--ts-surface-base-inverse);"
                    >
                        <div class="card-body py-4 py-lg-5 text-center vstack gap-2">
                            <div
                                class="rounded-circle d-inline-flex justify-content-center align-items-center mx-auto"
                                style="width: 4rem; height: 4rem; background-color: var(--ts-surface-subtle); color: var(--ts-content-subtle);"
                            >
                                <i class="bi bi-ticket-perforated fs-2"></i>
                            </div>
                            <h2 class="h4 fw-semibold mb-0">No gift vouchers are available right now</h2>
                            <p class="text-secondary mb-0">
                                Please check back later or contact the venue directly for gift voucher availability.
                            </p>
                        </div>
                    </div>
                }

                <p class="text-muted text-center mt-auto mb-0 small">
                    Powered by
                    <a
                        class="link-primary"
                        [href]="marketingUrl"
                        target="_blank"
                    >
                        TableSense
                    </a>
                </p>
            </div>
        </div>
    </ng-container>
</app-loading>
